/**
 * The keyframe / timeline engine.
 *
 * Sampling is a pure function of (document, time). Nothing here knows about
 * rAF, canvas or the clock — playback is the caller's loop feeding a time in.
 * That is what makes playback identical between the live board, a scrubbed
 * preview and any future deterministic video renderer.
 */

import type { Vec2 } from './geometry.ts';
import type { Id } from './ids.ts';
import type { Keyframe, PlayDocument, Shape } from './document.ts';
import { alongRoute, choreographedAt, choreographyInto, lanePoints, routeLength } from './lanes.ts';

export type PoseMap = Readonly<Record<Id, Vec2>>;

/** Milliseconds a piece takes to cross one field length. Every piece moves at this one pace. */
export const PACE_MS_PER_FIELD = 2600;
/**
 * The shortest a transition lasts, even when nothing in it moves far. It sets
 * no piece's speed — a short move still ends early — it only keeps two
 * phases from starting at the same instant, where the board could not tell
 * which of them is on screen.
 */
export const MIN_TRANSITION_MS = 500;

export interface Timeline {
  /** Start time of each phase in ms, index-aligned with `doc.keyframes`. */
  readonly starts: readonly number[];
  readonly total: number;
  /** What distance was measured in, so sampling moves every piece at the same pace. */
  readonly aspectRatio: number;
  readonly paceMs: number;
}

export interface TimelineOptions {
  /** Field width ÷ height, so distance is measured in the real shape of the field. */
  readonly aspectRatio: number;
  /** Milliseconds for one field length. Defaults to `PACE_MS_PER_FIELD`. */
  readonly paceMs?: number;
}

/**
 * When each phase starts, derived rather than authored.
 *
 * Every piece moves at one constant pace, so a transition lasts as long as
 * its longest route needs: a shuffle is quick, a length of the pool takes
 * longer, and nothing is sped up or slowed down to fit. A piece with a shorter
 * route arrives early and waits.
 */
export function buildTimeline(doc: PlayDocument, options: TimelineOptions): Timeline {
  // Unreachable with today's one caller (a pack's own ratio), but a pack
  // shipping without one would otherwise poison every downstream playhead
  // comparison with NaN rather than just running at a square aspect ratio.
  const aspectRatio = Number.isFinite(options.aspectRatio) ? options.aspectRatio : 1;
  const pace = Number.isFinite(options.paceMs) ? (options.paceMs as number) : PACE_MS_PER_FIELD;
  const starts: number[] = [];
  let at = 0;

  doc.keyframes.forEach((frame, index) => {
    if (index > 0) {
      const previous = doc.keyframes[index - 1]!;
      // Pieces moved by strokes take as long as their schedule says, detours
      // and waiting included; everything else takes the straight line.
      const choreography = choreographyInto(doc, frame.id, { aspectRatio, paceMs: pace });
      let finish = choreography.finish;
      for (const entity of doc.entities) {
        if (choreography.choreographed.has(entity.id)) continue;
        const a = previous.positions[entity.id];
        const b = frame.positions[entity.id];
        // A piece known to only one of the two phases holds still.
        if (!a || !b) continue;
        finish = Math.max(finish, routeLength([a, b], aspectRatio) * pace);
      }
      // Round before the floor: distances land on binary floats (e.g. 0.7 - 0.2), and a
      // span in whole milliseconds shouldn't wobble a fraction below a clean value.
      const span = Math.max(MIN_TRANSITION_MS, Math.round(finish));
      at += span;
    }
    starts.push(at);
  });

  return { starts, total: starts[starts.length - 1] ?? 0, aspectRatio, paceMs: pace };
}

/** Total play length in ms. */
export function duration(timeline: Timeline): number {
  return timeline.total;
}

/** The pair of phases surrounding `t`, plus the 0..1 blend between them. */
export function segmentAt(
  doc: PlayDocument,
  timeline: Timeline,
  t: number,
): { from: Keyframe; to: Keyframe; progress: number; elapsed: number } | undefined {
  const frames = doc.keyframes;
  const first = frames[0];
  if (!first) return undefined;
  const last = frames[frames.length - 1]!;
  if (t <= 0) return { from: first, to: first, progress: 0, elapsed: 0 };
  if (t >= timeline.total) return { from: last, to: last, progress: 1, elapsed: 0 };

  for (let i = 1; i < frames.length; i += 1) {
    const start = timeline.starts[i]!;
    if (t <= start) {
      const from = frames[i - 1]!;
      const previous = timeline.starts[i - 1]!;
      const span = start - previous;
      return { from, to: frames[i]!, progress: span === 0 ? 1 : (t - previous) / span, elapsed: t - previous };
    }
  }
  return { from: last, to: last, progress: 1, elapsed: 0 };
}

/**
 * Positions of every entity at time `t`. Every piece moves at the timeline's
 * one pace. A piece moved by strokes follows them in the order and at the
 * times `choreographyInto` schedules, waiting where it has to; everything else
 * takes the straight line from the start of the transition. A piece whose
 * moves are done arrives early and waits. Entities missing from one of the
 * two surrounding keyframes hold their known position rather than jumping to
 * origin.
 */
export function sampleAt(doc: PlayDocument, timeline: Timeline, t: number): PoseMap {
  const segment = segmentAt(doc, timeline, t);
  if (!segment) return {};

  const { from, to, elapsed } = segment;
  const poses: Record<Id, Vec2> = {};
  // At either end of the play `from` and `to` are the same phase: nothing moves.
  const choreography = from === to ? undefined : choreographyInto(doc, to.id, timeline);

  for (const entity of doc.entities) {
    const a = from.positions[entity.id];
    const b = to.positions[entity.id];
    if (a && b && choreography?.choreographed.has(entity.id)) {
      poses[entity.id] = choreographedAt(choreography, entity.id, a, elapsed, timeline);
    } else if (a && b) poses[entity.id] = from === to ? a : alongRoute([a, b], elapsed, timeline).at;
    else if (a) poses[entity.id] = a;
    else if (b) poses[entity.id] = b;
  }
  return poses;
}

/** The phase on screen at `t`: the last one that has started. -1 with no phases. */
export function phaseIndexAt(doc: PlayDocument, timeline: Timeline, t: number): number {
  let index = 0;
  timeline.starts.forEach((start, i) => {
    if (t >= start) index = i;
  });
  return doc.keyframes.length === 0 ? -1 : index;
}

/**
 * The phase whose drawings are on screen at `t`: the one being swum into, or
 * the one parked on. A lane describes the move into its phase, so it shows
 * while that move plays and stays once the phase is reached — which is also
 * where the coach drafted it.
 */
export function drawingPhaseIndexAt(doc: PlayDocument, timeline: Timeline, t: number): number {
  if (doc.keyframes.length === 0) return -1;
  // The same tolerance as `isOnKeyframe`: a playhead parked on a start owns it.
  const index = timeline.starts.findIndex((start) => t <= start + 1e-6);
  return index < 0 ? doc.keyframes.length - 1 : index;
}

/**
 * The drawings on screen at `t` (see `drawingPhaseIndexAt`), lanes drawn along
 * the route actually taken — so what is on screen is what the pieces swim.
 */
export function visibleShapes(doc: PlayDocument, timeline: Timeline, t: number): Shape[] {
  const frame = doc.keyframes[drawingPhaseIndexAt(doc, timeline, t)];
  if (!frame) return [];
  return doc.shapes
    .filter((s) => s.keyframeId === frame.id)
    .map((s) => (s.moverId === undefined ? s : { ...s, points: lanePoints(doc, s) }));
}

/** True when `t` sits exactly on a phase start (within `epsilonMs`). */
export function isOnKeyframe(timeline: Timeline, t: number, epsilonMs = 1): boolean {
  return timeline.starts.some((start) => Math.abs(start - t) <= epsilonMs);
}

/* ------------------------------------------------------------------ */
/* Stepping through phases (Space / Shift+Space)                       */
/* ------------------------------------------------------------------ */

/** The next phase start after `t` going forward (1) or before it going back (-1). */
export function nextStart(timeline: Timeline, t: number, direction: 1 | -1): number | undefined {
  const epsilon = 1e-6;
  if (direction > 0) return timeline.starts.find((start) => start > t + epsilon);
  return [...timeline.starts].reverse().find((start) => start < t - epsilon);
}

/**
 * What a tap does. `moving` is where playback is heading, or null when the
 * board is still. A quick press never waits for an animation:
 *
 *  - still: animate one phase in the tap's direction;
 *  - moving the same way: finish that move at once (`snapTo`), then animate
 *    one phase further — so quick taps shuffle through the play;
 *  - moving the other way: animate back to the phase it left.
 *
 * Nothing at either end of the play.
 */
export function tapStep(
  timeline: Timeline,
  playhead: number,
  moving: number | null,
  direction: 1 | -1,
): { readonly snapTo?: number; readonly target?: number } | undefined {
  const heading = moving === null ? 0 : Math.sign(moving - playhead);
  if (heading === direction) {
    const target = nextStart(timeline, moving!, direction);
    return target === undefined ? { snapTo: moving! } : { snapTo: moving!, target };
  }
  const target = nextStart(timeline, playhead, direction);
  return target === undefined ? undefined : { target };
}

/**
 * Where a held key's playback stops once it is let go: at the end of the move
 * under way, so the board always comes to rest on a phase. Already on one, it
 * stays there.
 */
export function releaseTarget(timeline: Timeline, playhead: number, direction: 1 | -1): number {
  if (isOnKeyframe(timeline, playhead)) return playhead;
  return nextStart(timeline, playhead, direction) ?? (direction > 0 ? timeline.total : 0);
}
